<template>
  <main v-if='!kql.loading' ref='targetElement' :class='{ "is-book": kql.page.pagetype === "book", "search-active": search.active }'>
    <div class='event-actions' v-if="kql.page.pagetype === 'event' || kql.page.pagetype === 'exhibition'">
      <span class='event-tag'>{{ kql.page.pagetype === 'exhibition' ? 'Exhibition' : 'Event' }}</span>
      <a v-if='eventIcalUrl !== "#"' class='event-ical' :href='eventIcalUrl' download>› Add to Calendar</a>
    </div>
    <header>
      <h1 v-if='kql.page.title' v-html='kql.page.title'/><h1 v-if='kql.page.appendix' v-html='kql.page.appendix'/>
    </header>
    <div class='event-meta' v-if="kql.page.pagetype === 'event' || kql.page.pagetype === 'exhibition'">
      <p v-if='eventMetaLocation'>{{ eventMetaLocation }}</p>
      <p v-if='eventMetaDate'>{{ eventMetaDate }}</p>
    </div>
    <Layout :content='kql.page.layout' :page='kql.page' fill='white'/>
  </main>
</template>

<script setup>
import Layout from '../components/Layout.vue'
import MainHeader from '../components/MainHeader.vue'
import { useStore } from '.././KQL.js'
import { useSearchStore } from '../components/SearchStore.js'
import { onMounted, onUnmounted, watch, ref, computed } from 'vue'
const search = useSearchStore();
//import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { clearBodyLocks, lock, unlock } from 'tua-body-scroll-lock'
const props = defineProps(['origin']);

const kql = useStore();

const eventMetaLocation = computed(() =>
  kql.page.location || kql.page.exhibitionlocation || ''
);

const fmtTime = t => {
  const [h, m] = t.split(':');
  const hour = parseInt(h);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const hour12 = hour % 12 || 12;
  const mins = m !== '00' ? `:${m}` : '';
  return `${hour12}${mins}${ampm}`;
};

const eventMetaDate = computed(() => {
  const p = kql.page;
  if (!p) return '';

  if (p.pagetype === 'exhibition') {
    if (!p.startdate) return '';
    const startDate = new Date(p.startdate + 'T12:00:00');
    const endDate = p.enddate ? new Date(p.enddate + 'T12:00:00') : null;
    const startStr = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    if (!endDate) return startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return `${startStr}–${endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
  }

  const dateStr = p.eventdate || '';
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T12:00:00');
  const dayDate = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const timeStr = p.eventtime || '';
  if (!timeStr) return dayDate;
  const startTime = fmtTime(timeStr);
  const endTimeStr = p.eventendtime || '';
  if (!endTimeStr) return `${dayDate}, ${startTime}`;
  return `${dayDate}, ${startTime}–${fmtTime(endTimeStr)}`;
});

const eventIcalUrl = computed(() => {
  const p = kql.page;
  const dateStr = p?.eventdate || p?.startdate || '';
  return dateStr ? `/ics/${p.uid}.ics` : '#';
});

const emit = defineEmits(['reasonsToShowTheVeil', 'changeColors', 'pageToGoBackTo'])
const targetElement = ref(null);
const body = ref(null);
const pageToGoBackTo = ref(false);

onMounted(() => {
  emit('reasonsToShowTheVeil', 1);
  document.querySelector('body').style.overflow = 'hidden';
  if (targetElement.value) {
    lock();
  }
});
onUnmounted(() => {
  emit('reasonsToShowTheVeil', -1);
  clearBodyLocks();
  document.querySelector('body').style.overflow = 'inherit';
});

watch(() => kql.page.startcolor, () => {
  emit('changeColors', kql.page.startcolor, kql.page.endcolor);
});

watch(() => kql.page.uid, () => {
  if(!pageToGoBackTo.value){
    pageToGoBackTo.value = kql.page.uid;
    emit('pageToGoBackTo', kql.page.uid);
  }
});

</script>

<style scoped>
@import '../css/breakpoints.css';

  main#Overlay {
    --border: rgba(255,255,255,0.5);
    position: fixed;
    padding-top: 17.5rem;
    padding-bottom: 12rem;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    overflow-y: scroll;
    overflow-x: hidden;
    padding-left: 12rem;
    padding-right: 12rem;
    z-index: 1;
    color: #f1f2f2;
  }

  .event-meta {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    margin-bottom: 3rem;
    font-size: 1rem;
    font-family: "Maison Neue Demi", sans-serif;
    opacity: 0.8;
    letter-spacing: 0.01em;
  }

  .event-actions {
    display: flex;
    align-items: center;
    gap: 0.6em;
    margin-left: -0.15em;
    margin-bottom: 0.4rem;
    font-size: 1rem;
  }

  .event-tag {
    font-family: "Maison Neue Demi", sans-serif;
    background: rgba(255, 255, 255, 0.15);
    color: inherit;
    border-radius: 2em;
    padding: 0.15em 0.8em;
    letter-spacing: 0.02em;
  }

  .event-meta p {
    margin: 0;
  }

  .event-ical {
    font-family: "Maison Neue Demi", sans-serif;
    color: inherit;
    text-decoration: none;
    opacity: 0.7;
  }

  .event-ical:hover {
    opacity: 1;
  }

  main.search-active {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease;
  }

  main {
    transition: opacity 0.25s ease;
  }

  main#Overlay hr {
    border-top-color: #f1f2f2;
    opacity: 0.666;
  }

  @media (--phone) {
    main#Overlay {
      padding-left: 4rem;
      padding-right: 4rem;
      padding-top: 17.5rem;
      padding-bottom: 12rem;
    }

    .event-meta {
      font-size: 1.5rem;
    }
  }

  summary {
    font-size: 2em;
  }

  img {
    width: 100%;
    vertical-align: bottom;
  }

  header {
    display: flex;
    justify-content: space-between;
  }

  h1 {
    font-size: 4rem;
    margin: 0 0 1rem;
  }

  main.is-book {
    padding-top: 16rem;
  }

  main.is-book h1 {
    margin-top: 0;
    margin-bottom: 4rem;
  }

  main {
    overflow-scrolling: touch;
  }
</style>

<style>
  @media print {

    main.Home > footer, main.Home > aside{
      display: none !important;
    }

    main.Home > header {
      position: absolute;
    }

    hr {
      display: none !important;
    }

    main#Overlay {
      opacity: 1;
      overflow: visible !important;
      position: static !important;
      bottom: auto !important;
      height: auto !important;
      max-height: auto !important;
      z-index: 1000;
    }
  }
</style>
